import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import { ServiceWishlist } from '@services/ServiceWishlist';
import { ServiceCart, addV1ApiDto, removeApiDto } from '@services/ServiceCart';
import { RootState } from '@redux/store';
import { ProductManagementState, addWishListTypeQuery } from './TypeState';
import { TypePromiseInfo } from '@services/ServiceCart/TypePromise';

export const getAllInfoProduct = createAsyncThunk('productManagement/getAllInfoProduct', async (regionId: number) => {
  return await ServiceCart.getInfo(regionId);
});

export type TypeError = {
  errorCode: number;
  errorMessage: string;
  redirect: string;
  success: boolean;
};

export const addProductInBasket = createAsyncThunk<any, addV1ApiDto, { rejectValue: { data: TypeError; status: number } }>(
  'productManagement/addProductInBasket',
  async (query, thunkAPI) => {
    try {
      return await ServiceCart.addV1(query);
    } catch (error) {
      return thunkAPI.rejectWithValue({ data: error.response.data, status: error.response.status });
    }
  },
);

export const deleteProductFromBasket = createAsyncThunk(
  'productManagement/deleteProductFromBasket',
  async ({ productId, regionId }: removeApiDto) => {
    return await ServiceCart.delete(productId, regionId);
  },
);

export const addProductInWishlist = createAsyncThunk('productManagement/addProductInWishlist', async (query: addWishListTypeQuery) => {
  const { id, regionId, part } = query;
  return await ServiceWishlist.addWishlist(id, part, regionId);
});

export const deleteProductFromWishlist = createAsyncThunk(
  'productManagement/deleteProductFromWishlist',
  async (query: addWishListTypeQuery) => {
    const { id, regionId } = query;
    return await ServiceWishlist.deleteWishlist(id, regionId);
  },
);

const initialState: ProductManagementState = {
  cartList: [],
  compareList: [],
  wishList: [],
  isLoading: false,
  addToBasket: false,
  errorMessageAddToBasket: '',
  email: '',
};

export const productManagementSlice = createSlice({
  name: 'productManagement',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(HYDRATE, (state, action: any) => {
      return {
        ...state,
        ...action.payload.productManagement,
      };
    });
    builder
      .addCase(addProductInWishlist.pending, state => {
        state.isLoading = true;
      })
      .addCase(addProductInWishlist.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload) {
          state.wishList = action.payload;
        }
        state.isLoading = false;
      });
    builder
      .addCase(deleteProductFromWishlist.pending, state => {
        state.isLoading = true;
      })
      .addCase(deleteProductFromWishlist.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload) {
          state.wishList = action.payload;
        }
        state.isLoading = false;
      });
    builder
      .addCase(getAllInfoProduct.pending, state => {
        state.isLoading = true;
      })
      .addCase(getAllInfoProduct.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload) {
          const { wishList, cartList, compareList, email } = action.payload;
          state.wishList = wishList;
          state.cartList = cartList;
          state.compareList = compareList;
          state.email = email;
        }
        state.isLoading = false;
      });
    builder
      .addCase(addProductInBasket.pending, state => {
        state.isLoading = true;
        state.addToBasket = false;
      })
      .addCase(addProductInBasket.fulfilled, (state, action: PayloadAction<TypePromiseInfo>) => {
        if (action.payload) {
          const { cartList } = action.payload;
          state.cartList = cartList;
        }
        state.isLoading = false;
        state.addToBasket = true;
      });
    builder
      .addCase(deleteProductFromBasket.pending, state => {
        state.isLoading = true;
      })
      .addCase(deleteProductFromBasket.fulfilled, (state, action: PayloadAction<TypePromiseInfo>) => {
        if (action.payload) {
          const { cartList } = action.payload;
          state.cartList = cartList;
        }
        state.isLoading = false;
      });
  },
});

export const selectWishList = (state: RootState) => state.productManagement.wishList;
export const selectCompareList = (state: RootState) => state.productManagement.compareList;
export const selectCartList = (state: RootState) => state.productManagement.cartList;
